import type { AnalysisSpec, ChartTemplate } from '@lognai/plugin-analytics';
import { businessLabel } from '../../lib/user-facing';
import { recommendTemplate } from '../creator-helpers';

const descriptions: Record<ChartTemplate, string> = {
  metric_panel: 'Cards with shares and targets',
  percentage_bar: 'Stepped contributions to a total',
  share_bar: 'Segments of a total',
  bullet: 'Completion rates against a target',
  table: 'Ranked metrics in rows',
  area: 'Trends over time',
  horizontal_bar: 'Ranked categories',
  stacked_column: 'Group comparisons',
  donut: 'Share of a total',
  funnel: 'Ordered stages',
  pareto: 'Largest contributors',
  bubble: 'Three numeric measures',
  treemap: 'Category proportions',
  sankey: 'Flows between categories',
  stacked_area: 'Components over time',
  gantt: 'Dated work and milestones',
  diverging_bar: 'Opposing measures',
  heatmap: 'Two breakdowns',
  calendar_heatmap: 'Daily patterns',
};

function ChartThumbnail({ template }: { template: ChartTemplate }) {
  const time = ['area', 'stacked_area', 'calendar_heatmap'].includes(template);
  const grid = ['heatmap', 'treemap', 'calendar_heatmap'].includes(template);
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 60 36"
      className="h-8 w-14 shrink-0 text-primary"
      fill="currentColor"
    >
      {template === 'donut' ? (
        <>
          <circle
            cx="30"
            cy="18"
            r="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            opacity=".2"
          />
          <circle
            cx="30"
            cy="18"
            r="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeDasharray="48 76"
            transform="rotate(-90 30 18)"
          />
        </>
      ) : grid ? (
        [0, 1, 2, 3, 4, 5].map((item) => (
          <rect
            key={item}
            x={6 + (item % 3) * 16}
            y={4 + Math.floor(item / 3) * 15}
            width="14"
            height="13"
            rx="2"
            opacity={0.25 + item * 0.13}
          />
        ))
      ) : time ? (
        <>
          <path d="M4 30 L4 23 L16 17 L28 21 L40 7 L56 3 L56 30 Z" opacity=".2" />
          <path
            d="M4 23 L16 17 L28 21 L40 7 L56 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          />
        </>
      ) : template === 'bubble' ? (
        <>
          <circle cx="13" cy="25" r="5" opacity=".4" />
          <circle cx="30" cy="17" r="9" opacity=".7" />
          <circle cx="48" cy="9" r="5" />
        </>
      ) : template === 'sankey' ? (
        <>
          <path
            d="M10 7 C32 7 28 28 50 28 M10 28 C32 28 28 7 50 7"
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            opacity=".4"
          />
          <rect x="5" y="3" width="5" height="30" rx="1" />
          <rect x="50" y="3" width="5" height="30" rx="1" />
        </>
      ) : template === 'funnel' ? (
        <>
          <path d="M5 4 H55 L47 12 H13 Z" />
          <path d="M13 15 H47 L41 23 H19 Z" opacity=".65" />
          <path d="M19 26 H41 L36 33 H24 Z" opacity=".35" />
        </>
      ) : ['stacked_column', 'diverging_bar'].includes(template) ? (
        [0, 1, 2, 3].map((item) => (
          <g key={item}>
            <rect
              x={7 + item * 13}
              y={13 - item * 2}
              width="8"
              height={18 + item * 2}
              rx="1"
              opacity=".4"
            />
            <rect x={7 + item * 13} y={13 - item * 2} width="8" height="9" rx="1" />
          </g>
        ))
      ) : (
        [0, 1, 2].map((item) => (
          <rect
            key={item}
            x={template === 'gantt' ? 6 + item * 10 : 6}
            y={5 + item * 10}
            width={42 - item * 10}
            height="6"
            rx="2"
            opacity={1 - item * 0.25}
          />
        ))
      )}
    </svg>
  );
}

export function ChartChoices({
  templates,
  analysis,
  onChange,
  disabled,
}: {
  templates: ChartTemplate[];
  analysis: AnalysisSpec;
  onChange: (template: ChartTemplate) => void;
  disabled?: boolean;
}) {
  const recommended = recommendTemplate(templates, analysis);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {templates.map((template) => (
          <button
            key={template}
            type="button"
            aria-pressed={analysis.template === template}
            disabled={disabled}
            className={`flex min-w-0 flex-wrap items-center gap-2 rounded-lg border p-3 text-left focus-visible:ring-2 focus-visible:ring-ring ${analysis.template === template ? 'border-primary bg-primary-subtle' : 'border-border hover:bg-muted'}`}
            onClick={() => onChange(template)}
          >
            <ChartThumbnail template={template} />
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-semibold">{businessLabel(template)}</span>
              <span className="mt-1 block text-[11px] text-muted-foreground">
                {template === recommended.template ? 'Recommended' : descriptions[template]}
              </span>
            </span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {templates.length
          ? recommended.reason
          : 'Select a metric and a breakdown or date grouping to see compatible charts.'}
      </p>
    </div>
  );
}
